import { useEffect, useRef } from "react";

const BLADES = ["#017729", "#02963A", "#00551D", "#2FA350", "#5DB86A"];
const POLLEN = "#EBCB00";

export const GrassInterlude = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf, W = 0, H = 0, blades = [], pollen = [], sway = 5, target = 5;
    const mouse = { x: -9999, y: -9999, active: false };
    const rnd = (v) => { const t = 43758.5453 * Math.sin(12.9898 * v); return t - Math.floor(t); };
    const setup = () => {
      const r = parent.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      blades = [];
      const step = W < 768 ? 7 : 5;
      for (let x = 0; x < W; x += step) {
        const e = rnd(x);
        blades.push({ x, hgt: H * (0.22 + 0.55 * e), ph: e * Math.PI * 2, col: BLADES[(e * BLADES.length) | 0], lean: 2 * (e - 0.5) });
      }
      pollen = Array.from({ length: W < 768 ? 12 : 30 }, () => ({ x: Math.random() * W, y: H, vy: 0.3 + 0.6 * Math.random(), life: Math.random() }));
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, W, H);
      sway += 0.05 * (target - sway);
      for (const b of blades) {
        let push = 0;
        if (mouse.active) {
          const dx = Math.abs(b.x - mouse.x);
          if (dx < 160) push = 20 * Math.sin(0.006 * t - 0.045 * dx) * (1 - dx / 160);
        }
        const bend = Math.sin(0.035 * b.x + 0.0013 * t + b.ph) * sway + push;
        const segs = Math.max(3, Math.floor(b.hgt / 6));
        for (let s = 0; s < segs; s++) {
          const e = s / segs;
          ctx.globalAlpha = 0.3 + 0.6 * e;
          ctx.fillStyle = b.col;
          ctx.fillRect(b.x + bend * e * 0.7 + b.lean * s * 0.4, H - 4 - 6 * s + bend * e, 3, 3);
        }
      }
      ctx.fillStyle = POLLEN;
      for (const p of pollen) {
        p.y -= p.vy; p.life -= 0.0028;
        if (p.y < 0.08 * H || p.life <= 0) { p.x = Math.random() * W; p.y = H - 4; p.life = 1; p.vy = 0.3 + 0.6 * Math.random(); }
        ctx.globalAlpha = 0.6 * p.life;
        ctx.fillRect(p.x + 4 * Math.sin(0.001 * t + p.x), p.y, 2.4, 2.4);
      }
      ctx.globalAlpha = 1;
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    const onMove = (e) => { const r = parent.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.active = true; target = 11; };
    const onLeave = () => { mouse.active = false; target = 5; };
    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", setup);
    setup();
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", setup);
    };
  }, []);
  return (
    <section data-testid="pixel-grass-interlude" className="relative h-[180px] sm:h-[230px] lg:h-[260px] overflow-hidden" aria-hidden="true">
      <canvas ref={ref} className="absolute inset-0" />
    </section>
  );
};

const SPARKS = ["#FFF4A3", "#FFDE00", "#EBCB00", "#C9EBB5", "#8FCF8A", "#FFFFFF"];

export const FieldCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf, W = 0, H = 0, pts = [], hover = 0;
    const mouse = { x: -9999, y: -9999, active: false };
    const setup = () => {
      const r = parent.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = W < 768 ? 900 : 2400;
      pts = Array.from({ length: n }, () => ({
        x: Math.random() * W, y: Math.random() * H, s: 1.2 + 2.4 * Math.random(), ph: Math.random() * Math.PI * 2,
        tw: 0.4 + 1.4 * Math.random(), drift: 0.15 + 0.5 * Math.random(), col: SPARKS[(Math.random() * SPARKS.length) | 0], base: 0.14 + 0.42 * Math.random(),
      }));
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, W, H);
      hover += 0.06 * ((mouse.active ? 1 : 0) - hover);
      for (const p of pts) {
        const tw = 0.65 + 0.35 * Math.sin(9e-4 * t * p.tw + p.ph);
        const x = p.x + 9 * Math.sin(4e-4 * t * p.drift + p.ph);
        const y = p.y + 7 * Math.cos(5e-4 * t * p.drift + 1.7 * p.ph);
        let a = p.base * tw, s = p.s;
        if (hover > 0.01) {
          const d = Math.hypot(x - mouse.x, y - mouse.y);
          if (d < 170) { const k = (1 - d / 170) * hover; a = Math.min(1, a + 0.85 * k); s = p.s * (1 + k); }
        }
        ctx.globalAlpha = Math.max(0, Math.min(1, a));
        ctx.fillStyle = p.col;
        ctx.fillRect(x, y, s, s);
      }
      ctx.globalAlpha = 1;
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    const onMove = (e) => { const r = parent.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.active = true; };
    const onLeave = () => { mouse.active = false; };
    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", setup);
    setup();
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", setup);
    };
  }, []);
  return <canvas ref={ref} data-testid="pixel-field-canvas" className="absolute inset-0 pointer-events-none" aria-hidden="true" />;
};
